import { forwardRef, type ComponentPropsWithoutRef } from 'react'
import * as RxCheckbox from '@radix-ui/react-checkbox'
import { cn } from './cn'

export type CheckboxProps = Omit<
  ComponentPropsWithoutRef<typeof RxCheckbox.Root>,
  'asChild'
>

/**
 * Themed Radix Checkbox.
 *
 * Three states reflected in the visual:
 *   unchecked      panel-2 fill, neutral border, inset top-highlight
 *   checked        accent fill, accent border, animated check stroke
 *   indeterminate  accent fill, animated horizontal bar (use `checked="indeterminate"`)
 *
 * Hover and focus shift the border to accent. Active shrinks the check
 * mark slightly (tactile press). All transitions use --dur-fast +
 * --ease-out-quart so the box feels alive without being flashy.
 */
export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ className, ...rest }, ref) => (
    <RxCheckbox.Root ref={ref} className={cn('checkbox', className)} {...rest}>
      {/* Indicator only mounts when state is checked or indeterminate
       * (no forceMount). Inside, both SVGs are rendered; CSS hides the
       * inactive one based on the root's data-state. Keeps the markup
       * simple and avoids stacking-context issues from absolute SVGs. */}
      <RxCheckbox.Indicator className="checkbox-indicator">
        <svg
          className="checkbox-check"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          aria-hidden="true"
        >
          <path
            d="M2 6.5L4.5 9L10 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <svg
          className="checkbox-indeterminate"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          aria-hidden="true"
        >
          <path
            d="M2 6 L10 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </RxCheckbox.Indicator>
    </RxCheckbox.Root>
  ),
)
Checkbox.displayName = 'Checkbox'
